import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

// Compact manual-order ticket for the currently selected symbol. Fires a REAL
// Tradovate bracket order (market entry + auto ATR stop / 2R target) after confirm.
export const ManualEntry = ({ symbol, connected, agentStops = false, onToggleStops }) => {
  const [qty, setQty] = useState(1);
  const [pending, setPending] = useState(null); // "LONG" | "SHORT" | null
  const [busy, setBusy] = useState(false);

  const fire = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const r = await api.orderManual({ symbol, direction: pending, qty: Number(qty) || 1 });
      const o = r?.order || {};
      if (r?.ok && r.result === "SENT_MANUAL") {
        toast.success(`LIVE ${pending} ${o.qty} ${symbol} SENT · self-managed (no agent stop)`,
          { className: "font-mono text-xs" });
      } else if (r?.ok && r.result === "SENT") {
        toast.success(`LIVE ${pending} ${o.qty} ${symbol} SENT · SL ${o.stop_loss} / TP ${o.take_profit}`,
          { className: "font-mono text-xs" });
      } else if (r?.result === "FILLED_UNPROTECTED") {
        toast.error(`${symbol} FILLED but UNPROTECTED — broker rejected SL/TP. FLATTEN now!`,
          { className: "font-mono text-xs", duration: 9000 });
      } else {
        toast.error(`${symbol} ${pending} rejected: ${r?.result || r?.error || "unknown"}`,
          { className: "font-mono text-xs", duration: 9000 });
      }
    } catch {
      toast.error("Order request failed", { className: "font-mono text-xs" });
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <div className="flex items-center gap-1.5 font-mono" data-testid="manual-entry">
      <span className="text-[9px] text-zinc-600 uppercase tracking-wider">Manual</span>
      <button
        data-testid="manual-agent-stops-toggle"
        onClick={() => onToggleStops && onToggleStops(!agentStops)}
        title={agentStops
          ? "Agent Stops ON — the agent manages & auto-closes your manual trades"
          : "Agent Stops OFF — manual trades run untouched; you manage the exit yourself"}
        className={`h-6 px-2 rounded text-[9px] font-bold uppercase tracking-wide border transition-colors ${
          agentStops
            ? "bg-amber-500/15 text-amber-400 border-amber-600/40 hover:bg-amber-500/25"
            : "bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700"}`}
      >
        {agentStops ? "AGENT STOPS: ON" : "AGENT STOPS: OFF"}
      </button>
      <Input
        type="number" min={1} max={5} value={qty}
        onChange={(e) => setQty(e.target.value)}
        data-testid="manual-qty"
        className="h-6 w-11 px-1 text-[11px] text-center bg-zinc-900 border-zinc-700 text-zinc-100"
      />
      <button
        data-testid="manual-long-btn" disabled={!connected || busy}
        onClick={() => setPending("LONG")}
        className="h-6 px-2.5 rounded text-[10px] font-semibold bg-green-600/20 text-green-400 border border-green-700/50 hover:bg-green-600/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >LONG</button>
      <button
        data-testid="manual-short-btn" disabled={!connected || busy}
        onClick={() => setPending("SHORT")}
        className="h-6 px-2.5 rounded text-[10px] font-semibold bg-red-600/20 text-red-400 border border-red-700/50 hover:bg-red-600/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >SHORT</button>
      {!connected && <span className="text-[9px] text-zinc-600">connect to trade</span>}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800 font-mono" data-testid="manual-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100 text-sm">Place LIVE {pending} order?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400 text-xs leading-relaxed">
              Real market order on your Tradovate account:{" "}
              <span className={pending === "LONG" ? "text-green-400 font-semibold" : "text-red-400 font-semibold"}>
                {pending} {qty} {symbol}
              </span>{" "}
              at market. {agentStops
                ? <>The agent will attach a <span className="text-zinc-300">stop/target and auto-manage the exit</span>.</>
                : <>The agent will <span className="text-amber-400">NOT manage or close this</span> — you set your own stop and exit it yourself.</>} <span className="text-zinc-300">This uses real money.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manual-cancel" className="text-xs bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="manual-confirm-btn" onClick={fire}
              className={`text-xs text-white ${pending === "LONG" ? "bg-green-600 hover:bg-green-500" : "bg-red-600 hover:bg-red-500"}`}
            >Confirm {pending}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
